import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {timeout} from 'rxjs/operators';
import {environment} from 'environments/environment';
import {AuthenticationService} from 'services/authentication/authentication.service';
import {IssueReport} from 'models/index';
import {CoverageReportChart, CoverageReportChartDrilldown, SearchProps, MyUserReport} from 'app/interfaces';
import {SearchService} from '../search/search.service';
import {EXPORT_TIMEOUT_MS} from 'app/constants';

@Injectable({
  providedIn: 'root',
})
export class MyCoverageService {
  private api: string;
  private httpOptions: any;
  issuesAvalibles: IssueReport[] = [];
  private userEmail: string;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private searchService: SearchService
  ) {
    this.api = environment.api_url;
    this.userEmail = this.authService.getUsername();
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
  }

  public getReportChart(
    projectId: number,
    coverageReportChart: CoverageReportChart
  ): Observable<any> {
    return this.http.post(
      `${this.api}/project/${projectId}/report/chart`,
      this.searchService.preProcessFilters(coverageReportChart),
      this.httpOptions
    );
  }

  public getCoverageReport(
    projectId: number,
    page: number,
    size: number,
    body: SearchProps
  ): Observable<any> {
    return this.http.post(
      `${this.api}/project/${projectId}/report/articles?page=${page}&size=${size}`,
      this.searchService.preProcessFilters(body),
      this.httpOptions
    );
  }

  public coverageReportExportToExcel(
    projectId: number,
    body: SearchProps
  ): Observable<Blob> {
    return this.http.post(
      `${this.api}/project/${projectId}/report/articles/export`,
      this.searchService.preProcessFilters(body),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        responseType: 'blob' as 'blob',
      }
    ).pipe(timeout(EXPORT_TIMEOUT_MS)) as Observable<Blob>;
  }

  public coverageReportExportToText(
    projectId: number,
    body: SearchProps
  ): Observable<string> {
    return this.http.post(
      `${this.api}/project/${projectId}/report/articles/dump`,
      this.searchService.preProcessFilters(body),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        responseType: 'text' as 'text',
      }
    ).pipe(timeout(EXPORT_TIMEOUT_MS)) as Observable<string>;
  }

  public getAuthorsReport(
    projectId: number,
    page: number,
    size: number,
    sort: string,
    body: SearchProps | CoverageReportChartDrilldown = { timeframe: 'ONE_YEAR' }
  ): Observable<any> {
    const sortParam = sort ? `&sort=${sort}` : '';
    const endpoint = 'selection' in body ? 'chart/authors' : 'authors';
    return this.http.post(
      `${this.api}/project/${projectId}/report/${endpoint}?page=${page}&size=${size}${sortParam}`,
      this.searchService.preProcessFilters(body),
      this.httpOptions
    );
  }

  public getReportsList(projectId: number): Observable<any> {
    return this.http.get(
      `${this.api}/project/${projectId}/reports`,
      this.httpOptions
    );
  }

  // Persists the complete list transactionally as contiguous order values (1..n).
  public updateReportOrder(
    projectId: number,
    orderedIds: number[]
  ): Observable<any> {
    return this.http.put(
      `${this.api}/project/${projectId}/reports/order`,
      orderedIds,
      this.httpOptions
    );
  }

  public createReport(projectId: number, report: MyUserReport): Observable<any> {
    return this.http.post(
      `${this.api}/project/${projectId}/reports`,
      report,
      this.httpOptions
    );
  }

  public deleteReport(projectId: number, reportId: number): Observable<any> {
    return this.http.delete(
      `${this.api}/project/${projectId}/reports/${reportId}`,
      this.httpOptions
    );
  }

  public updateReport(projectId: number, reportId: number, report: MyUserReport): Observable<any> {
    return this.http.put(
      `${this.api}/project/${projectId}/reports/${reportId}`,
      report,
      this.httpOptions
    );
  }

  public getOutletsReport(
    projectId: number,
    page: number,
    size: number,
    sort: string,
    body: SearchProps | CoverageReportChartDrilldown = { timeframe: 'ONE_YEAR' }
  ): Observable<any> {
    const sortParam = sort ? `&sort=${sort}` : '';
    const endpoint = 'selection' in body ? 'chart/outlets' : 'outlets';
    return this.http.post(
      `${this.api}/project/${projectId}/report/${endpoint}?page=${page}&size=${size}${sortParam}`,
      this.searchService.preProcessFilters(body),
      this.httpOptions
    );
  }
}
